import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import { useSelector } from "react-redux";
import { NavLink, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import VehiclesTable from "../../../component/admin/VehiclesTable";
import i18n from "../../../i8next";
import {
  useGetVehicleColorQuery,
  useGetVehicleMakeQuery,
  useGetVehicleTypeQuery,
} from "../../../store/api/configurationSlice";
import { useGetAllVehiclesQuery } from "../../../store/api/vendor/vehiclesSlice";
import "./v.scss";

const initialActions = {
  make_id: "",
  color_id: "",
  vehicle_type_id: "",
  search: "",
  role: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

const VehiclesList = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const { role, slug } = useParams();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);

  const [vehicles, setVehicles] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);

  const [vehicleInfo, setVehicleInfo] = useState({
    makes: [],
    colors: [],
    types: [],
  });

  // Get Role From React Redux
  const isAdmin = user?.role === "super_admin";
  // slug base fetch data
  const userRole =
    slug === "school-vehicles"
      ? "school"
      : slug === "vendor-vehicles"
        ? "vendor"
        : null;

  // Select the active result set
  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetAllVehiclesQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
      role: userRole,
    });

  // Get Vehicle Make
  const {
    data: makesData,
    isSuccess: isSuccessMakes,
    refetch: refetchMake,
  } = useGetVehicleMakeQuery({});

  // Get Vehicle Color
  const {
    data: colorData,
    isSuccess: isSuccessColor,
    refetch: refetchColor,
  } = useGetVehicleColorQuery({});

  // Get Vehicle Type
  const {
    data: typeData,
    isSuccess: isSuccessType,
    refetch: refetchType,
  } = useGetVehicleTypeQuery({});

  // Fetch All Vehicles
  useEffect(() => {
    if (isSuccess && data?.data) {
      setVehicles(data?.data?.vehicles);
      setPagination(data.data.pagination);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (isSuccessMakes && makesData?.data) {
      setVehicleInfo((prev) => ({ ...prev, makes: makesData.data.makes }));
    }

    if (isSuccessColor && colorData?.data) {
      setVehicleInfo((prev) => ({ ...prev, colors: colorData.data.colors }));
    }

    if (isSuccessType && typeData?.data) {
      setVehicleInfo((prev) => ({ ...prev, types: typeData.data.types }));
    }
  }, [
    isSuccessMakes,
    makesData,
    isSuccessColor,
    colorData,
    isSuccessType,
    typeData,
  ]);

  // filters chnages
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAction((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ limit: 10, page: 1 }));
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: search.trim() }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination(initialPagination);
    setSearch("");
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
      refetchMake?.();
      refetchColor?.();
      refetchType?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2 gap-2">
        <div className="total">
          {t("total")} {t("vehicles")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              name="make_id"
              value={action.make_id}
              onChange={handleChange}
            >
              <option value="">
                {t("select")} {t("make")}
              </option>
              {vehicleInfo.makes?.length > 0
                ? vehicleInfo.makes?.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))
                : ""}
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            {" "}
            <select
              className="form-select"
              name="color_id"
              value={action.color_id}
              onChange={handleChange}
            >
              <option value="">
                {t("select")} {t("color")}
              </option>
              {vehicleInfo.colors?.length > 0
                ? vehicleInfo.colors?.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))
                : ""}
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            {" "}
            <select
              className="form-select"
              name="vehicle_type_id"
              value={action.vehicle_type_id}
              onChange={handleChange}
            >
              <option value="">
                {t("select")} {t("vehicle")}
                {t("type")}
              </option>
              {vehicleInfo.types?.length > 0
                ? vehicleInfo.types?.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))
                : ""}
            </select>
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {!isAdmin && (
              <NavLink
                to="add"
                className="page-add-btn d-flex align-items-center justify-content-center gap-2"
              >
                <FaPlus /> {t("add")} {t("vehicle")}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <VehiclesTable
        vehicles={vehicles}
        isLoading={isLoading || isFetching}
        pagination={pagination}
        setPagination={setPagination}
      />
    </>
  );
};
export default VehiclesList;
